import { CreditCard, Wallet, AlertTriangle, Clock, TrendingUp, Shield, Activity, Info, CheckCircle2 } from "lucide-react";
import type { SimState } from "@/data/creditSim";
import { minPaymentFor, scoreTierLabel, utilizationPct } from "@/data/creditSim";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const money = (n: number) =>
  (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString();

function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="More info"
          className="inline-flex items-center justify-center text-muted-foreground/70 hover:text-primary transition-colors"
        >
          <Info className="w-3 h-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-[11px] leading-snug">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

function Row({
  icon, label, value, tone = "default", tip,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tone?: "good" | "warn" | "bad" | "default";
  tip?: string;
}) {
  const toneClass =
    tone === "good" ? "text-emerald-400"
    : tone === "warn" ? "text-amber-400"
    : tone === "bad" ? "text-destructive"
    : "text-foreground";
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground min-w-0">
        {icon}
        <span className="truncate">{label}</span>
        {tip ? <InfoTip text={tip} /> : null}
      </div>
      <p className={`font-mono text-[12px] font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

function SectionCard({
  title, children,
}: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-primary/20 bg-card/60 p-3 backdrop-blur-sm">
      <p className="font-heading text-[10px] font-bold tracking-wider uppercase text-primary/80 mb-2">
        {title}
      </p>
      {children}
    </div>
  );
}

function riskLabel(r: number): { text: string; tone: "good" | "warn" | "bad" } {
  if (r >= 70) return { text: "High", tone: "bad" };
  if (r >= 40) return { text: "Medium", tone: "warn" };
  return { text: "Low", tone: "good" };
}

export default function CreditDashboard({ s, phase }: { s: SimState; phase: string }) {
  const util = utilizationPct(s.balance, s.creditLimit);
  const minPay = minPaymentFor(s.balance);
  const available = Math.max(0, s.creditLimit - s.balance);
  const tier = scoreTierLabel(s.score);
  const utilTone = util >= 70 ? "bad" : util >= 30 ? "warn" : "good";
  const cashTone = s.checking < 0 ? "bad" : s.checking < minPay ? "warn" : "good";
  const risk = riskLabel(s.lateRisk);

  const health = Math.max(
    0,
    Math.min(100, 100 - util * 0.6 - s.missedPayments * 25 - s.lateRisk * 0.2),
  );
  const healthTone = health >= 70 ? "good" : health >= 40 ? "warn" : "bad";
  const scoreToneText =
    tier.tone === "good" ? "text-emerald-400"
    : tier.tone === "warn" ? "text-amber-400"
    : tier.tone === "bad" ? "text-destructive"
    : "text-foreground";

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between px-1">
          <p className="font-heading text-[11px] font-bold text-gradient-blue tracking-wide uppercase">
            Credit Mission (Simulated)
          </p>
          <span className="text-[10px] font-mono text-muted-foreground">{phase}</span>
        </div>

        {/* 1. Main Credit Status */}
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-card/90 to-card/60 p-4 backdrop-blur-md">
          <p className="font-heading text-[10px] font-bold tracking-wider uppercase text-primary/80 mb-3">
            Your Simulated Credit Card
          </p>

          <div className="flex items-end justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Activity className="w-3 h-3" />
                <span>Credit Score</span>
                <InfoTip text="A number from 300–850 that lenders use to predict how reliably you repay borrowed money." />
              </div>
              <p className={`font-heading font-bold leading-none mt-1 text-4xl ${scoreToneText}`}>
                {s.score}
              </p>
              <p className={`text-[11px] font-semibold mt-1 ${scoreToneText}`}>{tier.label}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Balance Owed</p>
              <p className="font-mono text-lg font-bold text-foreground">{money(s.balance)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">
                of <span className="font-mono">{money(s.creditLimit)}</span> limit
                <span className="ml-1">
                  <InfoTip text="Your credit limit is the most you're allowed to borrow on this card." />
                </span>
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-border/60 bg-card/70 p-2 mb-2">
            <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
              <span>Credit Health</span>
              <span className="font-mono">{Math.round(health)}/100</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full transition-all ${
                  healthTone === "good" ? "bg-emerald-400" : healthTone === "warn" ? "bg-amber-400" : "bg-destructive"
                }`}
                style={{ width: `${health}%` }}
              />
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground/80 leading-snug">
            This is your simulated credit card profile. Your choices in this mission can raise or lower your credit health.
          </p>
        </div>

        {/* 2. Payment Status */}
        <SectionCard title="Payment Status">
          <div className="divide-y divide-border/40">
            <Row
              icon={<Clock className="w-3 h-3" />}
              label="Minimum Payment Due"
              value={money(minPay)}
              tip="The smallest amount you can pay this month to keep the account in good standing. Paying only the minimum lets interest grow."
            />
            <Row
              icon={<Clock className="w-3 h-3" />}
              label="Payment Due In"
              value={`${s.daysToDue} days`}
            />
            <Row
              icon={<Wallet className="w-3 h-3" />}
              label="Checking Balance"
              value={money(s.checking)}
              tone={cashTone}
            />
          </div>
          {minPay <= 0 ? (
            <div className="mt-2 flex items-center gap-1.5 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-2 py-1 text-[11px] text-emerald-400">
              <CheckCircle2 className="w-3 h-3" />
              <span>No payment due yet</span>
            </div>
          ) : null}
          <p className="text-[10px] text-muted-foreground/80 leading-snug mt-2">
            Your checking balance is the money available to make credit card payments.
          </p>
        </SectionCard>

        {/* 3. Credit Usage & Risk */}
        <SectionCard title="Credit Usage & Risk">
          <div className="divide-y divide-border/40">
            <Row
              icon={<TrendingUp className="w-3 h-3" />}
              label="Credit Used"
              value={`${util}%`}
              tone={utilTone}
              tip="The share of your credit limit you're currently using. Keeping this below 30% helps your score."
            />
            <Row
              icon={<Wallet className="w-3 h-3" />}
              label="Available Credit"
              value={money(available)}
            />
            <Row
              icon={<TrendingUp className="w-3 h-3" />}
              label="APR"
              value={`${s.apr}%`}
              tip="Annual Percentage Rate — the interest rate charged if you carry a balance instead of paying in full."
            />
            <Row
              icon={<AlertTriangle className="w-3 h-3" />}
              label="Missed Payments"
              value={`${s.missedPayments}`}
              tone={s.missedPayments > 0 ? "bad" : "good"}
            />
            <Row
              icon={<Shield className="w-3 h-3" />}
              label="Late Payment Risk"
              value={
                <span title={`${Math.round(s.lateRisk)}/100`}>{risk.text}</span>
              }
              tone={risk.tone}
              tip="An estimate of how likely a late payment is, based on your balance, due date, and cash on hand."
            />
            {s.interestAccrued > 0 ? (
              <Row
                icon={<TrendingUp className="w-3 h-3" />}
                label="Interest Charged"
                value={money(s.interestAccrued)}
                tone="warn"
              />
            ) : null}
          </div>
          <p className="text-[10px] text-muted-foreground/80 leading-snug mt-2">
            Using too much of your credit or missing payments can lower your score.
          </p>
        </SectionCard>

        {/* What to watch */}
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
          <p className="text-[10px] font-bold tracking-wider uppercase text-primary mb-1">
            What should I watch?
          </p>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Watch your balance, payment due date, and credit used. These are the main things that can change your credit health.
          </p>
        </div>

        <p className="text-[9px] text-muted-foreground/70 italic text-center">
          Simulated credit profile — for learning only. Does not affect real credit bureaus.
        </p>
      </div>
    </TooltipProvider>
  );
}